import {
	BottomSheetFooter,
	type BottomSheetFooterProps,
} from "@gorhom/bottom-sheet";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Dialog, List } from "react-native-paper";
import { type IconType, isIconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import Button from "../Button";
import DialogWrapper from "../DialogWrapper";
import ListBottomSheetWrapper from "../ListBottomSheetWrapper";
import { MasteryListItem } from "../MasteryListItem";

type SpecialityFilter = {
	title: string;
	icon: string;
	description: string;
	isSelected: boolean;
};

type Props = {
	showModal: boolean;
	handleSheetClose: () => void;
	specialitiesFilter: SpecialityFilter[];
	applyFilters: (filters: SpecialityFilter[]) => void;
	onOpen?: () => void;
	title?: string;
	icon?: IconType;
	presentation?: "sheet" | "dialog";
};

const FilterModalContainer = ({
	showModal,
	handleSheetClose,
	specialitiesFilter,
	applyFilters,
	onOpen,
	title = "Filter by specialties",
	icon,
	presentation = "sheet",
}: Props) => {
	useEffect(() => {
		if (showModal) {
			onOpen?.();
		}
	}, [showModal, onOpen]);

	const theme = useAppTheme();
	const { t } = useTranslation();
	const [tempFilters, setTempFilters] =
		useState<SpecialityFilter[]>(specialitiesFilter);

	const latestFiltersRef = useRef(specialitiesFilter);
	latestFiltersRef.current = specialitiesFilter;

	// Re-seed pending selections each time the modal opens so late-arriving
	// filter data is picked up without resetting edits mid-session.
	useEffect(() => {
		if (showModal) {
			setTempFilters(latestFiltersRef.current);
		}
	}, [showModal]);

	const handleToggleSpeciality = useCallback((speciality: string) => {
		setTempFilters((prev) =>
			prev.map((item) =>
				item.title === speciality
					? { ...item, isSelected: !item.isSelected }
					: item,
			),
		);
	}, []);

	const handleClearAll = useCallback(() => {
		setTempFilters((prev) =>
			prev.map((item) => ({ ...item, isSelected: false })),
		);
		handleSheetClose();
		applyFilters([]);
	}, [handleSheetClose, applyFilters]);

	const handleDone = useCallback(() => {
		applyFilters(tempFilters);
		handleSheetClose();
	}, [applyFilters, tempFilters, handleSheetClose]);

	const renderFooter = (p: BottomSheetFooterProps) => (
		<BottomSheetFooter
			{...p}
			style={{
				padding: 24,
				backgroundColor: theme.colors.surface,
			}}
		>
			<Button mode="contained" onPress={handleDone}>
				{t("common.done")}
			</Button>
		</BottomSheetFooter>
	);

	const filterItems = tempFilters.map((item) => {
		const itemIconProps = isIconType(item.icon)
			? { icon: item.icon }
			: { iconUrl: item.icon };
		return (
			<MasteryListItem
				key={item.title}
				title={item.title}
				{...itemIconProps}
				description={item.description}
				onPress={() => handleToggleSpeciality(item.title)}
				renderRight={(p) => {
					return (
						<List.Icon
							{...p}
							icon={() => {
								return (
									<Checkbox.Android
										status={item.isSelected ? "checked" : "unchecked"}
									/>
								);
							}}
						/>
					);
				}}
				distribution={{
					high: 0,
					medium: 0,
					low: 0,
					none: 0,
				}}
			/>
		);
	});

	if (presentation === "dialog") {
		return (
			<DialogWrapper
				visible={showModal}
				handleClose={handleSheetClose}
				width={520}
			>
				{icon ? <Dialog.Icon icon={icon} aria-label={title} /> : null}
				<Dialog.Title style={{ textAlign: icon ? "center" : "left" }}>
					{title}
				</Dialog.Title>
				<Box>{filterItems}</Box>
				<Box gap={12} paddingHorizontal={24} paddingTop={12} paddingBottom={24}>
					<Button mode="contained" onPress={handleDone}>
						{t("common.done")}
					</Button>
					<Button onPress={handleClearAll}>{t("common.clearAll")}</Button>
				</Box>
			</DialogWrapper>
		);
	}

	return (
		<ListBottomSheetWrapper
			showModal={showModal}
			title={title}
			snapPoints={["50%", "80%"]}
			handleSheetClose={handleSheetClose}
			paddingBottom={124}
			renderFooter={renderFooter}
			handleClearAll={handleClearAll}
		>
			<Box paddingTop={16}>{filterItems}</Box>
		</ListBottomSheetWrapper>
	);
};

export default FilterModalContainer;
